import {
  Box,
  Button,
  Center,
  Divider,
  Heading,
  HStack,
  Image,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  CheckIcon,
  DownloadIcon,
  EmailIcon,
  PhoneIcon,
} from '@chakra-ui/icons';

/**
 * Shown once the customer has signed (right after signing, or when they
 * reopen the link): the company's logo, what they agreed to, the company's
 * next steps (Settings > General), and a download of the signed copy.
 */
const SignedConfirmation = ({ token, session }) => {
  const company = session?.company || {};
  const summary = session?.summary;
  const firstName = (session?.customerName || '').trim().split(/\s+/)[0];
  const signedCopyUrl = `${process.env.REACT_APP_BACKEND_URL}/api/signing/${token}/signed-copy`;
  const hasChoice = summary && (summary.option || summary.upgrades?.length);

  const row = (label, amount, props) => (
    <HStack
      key={label}
      justify="space-between"
      align="flex-start"
      spacing={4}
      {...props}
    >
      <Text>{label}</Text>
      <Text flexShrink={0}>{amount}</Text>
    </HStack>
  );

  return (
    <Box minH="100vh" bg="gray.50" py={{ base: 6, md: 12 }} px={4}>
      <Box
        maxW="560px"
        mx="auto"
        bg="white"
        borderRadius="lg"
        boxShadow="md"
        overflow="hidden"
        borderTopWidth="6px"
        borderTopColor={company.color || 'green.500'}
      >
        <VStack align="stretch" spacing={6} p={{ base: 6, md: 8 }}>
          {company.logo ? (
            <Image
              src={company.logo}
              alt={company.name || 'Company logo'}
              maxH="90px"
              maxW="260px"
              mx="auto"
              objectFit="contain"
            />
          ) : (
            company.name && (
              <Heading size="md" textAlign="center">
                {company.name}
              </Heading>
            )
          )}

          <VStack spacing={3} textAlign="center">
            <Center w="64px" h="64px" borderRadius="full" bg="green.500">
              <CheckIcon boxSize={7} color="white" />
            </Center>
            <Heading size="lg">
              You're all set{firstName ? `, ${firstName}` : ''}!
            </Heading>
            <Text fontSize="lg" color="gray.600">
              We received your signed estimate
              {summary?.signedDate ? ` on ${summary.signedDate}` : ''}.
            </Text>
          </VStack>

          {hasChoice && (
            <Box bg="gray.50" borderRadius="md" px={4} py={3}>
              <VStack align="stretch" spacing={1} fontSize="md">
                {summary.option &&
                  row(summary.option.label, summary.option.amount)}
                {summary.upgrades.map(upgrade =>
                  row(`+ ${upgrade.label}`, upgrade.amount, {
                    color: 'gray.700',
                  })
                )}
                <Divider pt={1} />
                {row('Total', summary.total, {
                  pt: 1,
                  fontWeight: 'bold',
                  fontSize: 'xl',
                })}
              </VStack>
            </Box>
          )}

          {company.nextSteps?.length > 0 && (
            <Box>
              <Heading size="md" mb={3}>
                What happens next
              </Heading>
              <VStack as="ol" align="stretch" spacing={3} listStyleType="none">
                {company.nextSteps.map((step, index) => (
                  <HStack as="li" key={index} align="flex-start" spacing={3}>
                    <Center
                      flexShrink={0}
                      w="28px"
                      h="28px"
                      borderRadius="full"
                      bg="blue.50"
                      color="blue.700"
                      fontWeight="bold"
                      fontSize="sm"
                    >
                      {index + 1}
                    </Center>
                    <Text fontSize="md" pt="2px">
                      {step}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}

          <Box>
            <Button
              as="a"
              href={signedCopyUrl}
              target="_blank"
              rel="noopener noreferrer"
              colorScheme="blue"
              size="lg"
              w="100%"
              leftIcon={<DownloadIcon />}
            >
              Download Signed Copy
            </Button>
            {session?.customerEmail && (
              <Text fontSize="sm" color="gray.600" textAlign="center" mt={2}>
                A copy was also emailed to {session.customerEmail}.
              </Text>
            )}
          </Box>

          {(company.phone || company.email) && (
            <Box borderTopWidth="1px" pt={5}>
              <Text fontWeight="bold" mb={3} textAlign="center">
                Questions? Contact {company.name || 'us'}
              </Text>
              <Stack direction={{ base: 'column', sm: 'row' }} spacing={3}>
                {company.phone && (
                  <Button
                    as="a"
                    href={`tel:${company.phone.replace(/[^\d+]/g, '')}`}
                    variant="outline"
                    leftIcon={<PhoneIcon />}
                    flex={1}
                  >
                    {company.phone}
                  </Button>
                )}
                {company.email && (
                  <Button
                    as="a"
                    href={`mailto:${company.email}`}
                    variant="outline"
                    leftIcon={<EmailIcon />}
                    flex={1}
                    overflow="hidden"
                  >
                    <Text isTruncated>{company.email}</Text>
                  </Button>
                )}
              </Stack>
            </Box>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default SignedConfirmation;
